/**
 * Fleet auth path — DEV ONLY, used when resolveAuthMode() === 'fleet'.
 *
 * ballwatcherdev sits behind the fleet OAuth2 Proxy. The proxy owns the login:
 * it authenticates the browser with its own HttpOnly cookie and, on every
 * request it forwards, injects `Authorization: Bearer <Google ID token>` for
 * the backend to verify. This module therefore never sees a credential:
 *
 * - Identity is restored by asking the backend who we are (`GET /api/me`);
 *   the browser attaches the proxy cookie by itself.
 * - `AuthSession.idToken` / `getIdToken()` return the fixed placeholder below
 *   so the existing `Authorization: Bearer …` and SSE `?token=…` plumbing keeps
 *   working unchanged. The placeholder is NOT a credential: Caddy strips the
 *   inbound Authorization header, the proxy writes the real one, and the
 *   backend ignores `?token=` entirely in fleet mode.
 * - Nothing is persisted: no localStorage, no sessionStorage, no token in a URL
 *   or a log line. A reload simply asks `/api/me` again.
 *
 * The Firebase Web SDK is never imported on this path.
 */
import type { AuthErrorInfo, AuthSession } from './types';

export const FLEET_SESSION_TOKEN = 'fleet-session';
export const FLEET_SIGN_IN_URL = '/oauth2/sign_in';
/** Ends the proxy session, then lands on the proxy's own sign-in page. */
export const FLEET_SIGN_OUT_URL = '/oauth2/sign_out?rd=/oauth2/sign_in';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');
const ERR_RE = /ERR_[A-Z0-9]{8}/;
/** Backend code for "genuine Google account, not on the dev allowlist". */
const NOT_ALLOWED_CODE = 'ERR_AUTH0007';

export type FleetRestoreResult =
  | { status: 'signed-in'; session: AuthSession }
  | { status: 'signed-out' }
  | { status: 'error'; error: AuthErrorInfo };

function fail(errId: string, message: string, context: Record<string, unknown>): FleetRestoreResult {
  // Every error shown to the user is logged under the same errId. Context is
  // status codes / error names only — never response bodies or identities.
  // eslint-disable-next-line no-console
  console.error('fleetAuth:restore', { op: 'fleet-restore', errId, ts: Date.now(), ...context });
  return { status: 'error', error: { errId, message: `${message} (${errId})` } };
}

function parseSession(bodyText: string): AuthSession | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(bodyText);
  // intentional swallow: a non-JSON 200 (e.g. an HTML page from a proxy in
  // front of us) is not lost — the caller reports it as ERR_FLEET003 and logs it.
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;
  const { uid, email, display_name: displayName } = parsed as Record<string, unknown>;
  if (typeof uid !== 'string' || uid === '') return null;
  if (email !== null && typeof email !== 'string') return null;
  if (displayName !== null && displayName !== undefined && typeof displayName !== 'string') return null;
  return {
    user: { uid, email, displayName: displayName ?? null },
    idToken: FLEET_SESSION_TOKEN,
  };
}

export async function fleetRestoreSession(): Promise<FleetRestoreResult> {
  let resp: Response;
  try {
    resp = await fetch(`${API_BASE}/api/me`, {
      method: 'GET',
      // The proxy cookie IS the login — it must ride along (same-origin only).
      credentials: 'same-origin',
      // An expired proxy session answers with a redirect towards Google. Don't
      // chase it cross-origin; surface it as "signed out" instead.
      redirect: 'manual',
      cache: 'no-store',
      // Asks OAuth2 Proxy for a 401 rather than its HTML sign-in page.
      headers: { accept: 'application/json' },
    });
  } catch (e) {
    return fail('ERR_FLEET001', "We couldn't reach Ball Watcher to check your sign-in. Check your connection and try again.", {
      error: e instanceof Error ? `${e.name}: ${e.message}` : String(e),
      stack: e instanceof Error ? e.stack : undefined,
    });
  }

  if (resp.type === 'opaqueredirect' || resp.status === 401) return { status: 'signed-out' };

  const bodyText = await resp.text();
  if (resp.status === 403) {
    // Two different 403s: the backend's coded "not on the allowlist", and the
    // proxy's HTML sign-in page (which it serves with a 403).
    if (bodyText.match(ERR_RE)?.[0] === NOT_ALLOWED_CODE) {
      return fail(
        NOT_ALLOWED_CODE,
        "You're signed in to Google, but this account isn't on the Ball Watcher dev allowlist. Ask Eric to add it, or sign in with a different account.",
        { httpStatus: 403 },
      );
    }
    return { status: 'signed-out' };
  }
  if (!resp.ok) {
    return fail('ERR_FLEET002', 'Ball Watcher had a problem checking your sign-in. Try again in a moment.', {
      httpStatus: resp.status,
      backendErrId: bodyText.match(ERR_RE)?.[0],
    });
  }

  const session = parseSession(bodyText);
  if (!session) {
    return fail('ERR_FLEET003', 'Ball Watcher sent back a sign-in response we could not read. Try again in a moment.', {
      httpStatus: resp.status,
    });
  }
  return { status: 'signed-in', session };
}

export async function fleetGetIdToken(): Promise<string> {
  return FLEET_SESSION_TOKEN;
}

/** Full-page navigations: the proxy, not the SPA, runs the OAuth flow. */
export function fleetSignIn(): void {
  window.location.assign(FLEET_SIGN_IN_URL);
}

export function fleetSignOut(): void {
  window.location.assign(FLEET_SIGN_OUT_URL);
}
